import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { apiHelpers } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  BookOpenIcon,
  SparklesIcon,
  RocketLaunchIcon,
  CircleStackIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  WrenchScrewdriverIcon,
  QuestionMarkCircleIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Category icons and colors
const CATEGORY_META = {
  getting_started: {
    icon: RocketLaunchIcon,
    color: 'from-primary-400 to-primary-600',
    bgLight: 'bg-primary-50 dark:bg-primary-900/20',
    textColor: 'text-primary-600 dark:text-primary-400'
  },
  data_sources: {
    icon: CircleStackIcon,
    color: 'from-blue-400 to-blue-600',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400'
  },
  dashboards: {
    icon: ChartBarIcon,
    color: 'from-indigo-400 to-purple-600',
    bgLight: 'bg-indigo-50 dark:bg-indigo-900/20',
    textColor: 'text-indigo-600 dark:text-indigo-400'
  },
  ai_assistant: {
    icon: SparklesIcon,
    color: 'from-purple-400 to-pink-600',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
    textColor: 'text-purple-600 dark:text-purple-400'
  },
  billing: {
    icon: CreditCardIcon,
    color: 'from-green-400 to-emerald-600',
    bgLight: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400'
  },
  troubleshooting: {
    icon: WrenchScrewdriverIcon,
    color: 'from-orange-400 to-red-600',
    bgLight: 'bg-orange-50 dark:bg-orange-900/20',
    textColor: 'text-orange-600 dark:text-orange-400'
  }
};

// Custom ReactMarkdown components for YouTube embedding and styling
const markdownComponents = {
  a: ({ href, children, ...props }) => {
    const youtubeMatch = href?.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/
    );

    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      return (
        <div className="my-4 aspect-video max-w-2xl">
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/${videoId}`}
            title={typeof children === 'string' ? children : 'YouTube video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="rounded-lg"
          />
        </div>
      );
    }

    const isExternal = href?.startsWith('http');
    return (
      <a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className="text-primary-600 dark:text-primary-400 hover:underline"
        {...props}
      >
        {children}
      </a>
    );
  }
};

export default function Documentation() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();

  // State
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const searchTimeout = useRef(null);

  // Read slug from URL params
  const activeSlug = searchParams.get('doc');

  // Fetch all published docs grouped by category
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiHelpers.getHelpDocs();
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load documentation:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch a single document by slug
  const loadDocument = useCallback(async (slug) => {
    try {
      setDocLoading(true);
      const response = await apiHelpers.getHelpDoc(slug);
      setSelectedDoc(response.data.document);
    } catch (error) {
      console.error('Failed to load document:', error);
      setSelectedDoc(null);
      // Remove invalid slug from URL
      setSearchParams({});
    } finally {
      setDocLoading(false);
    }
  }, [setSearchParams]);

  // Search documents
  const handleSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults(null);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await apiHelpers.searchHelpDocs(query.trim());
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      handleSearch(searchQuery);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, handleSearch]);

  // Load all docs on mount
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Load specific doc when slug changes
  useEffect(() => {
    if (activeSlug) {
      loadDocument(activeSlug);
    } else {
      setSelectedDoc(null);
    }
  }, [activeSlug, loadDocument]);

  // Open a document
  const openDoc = (slug) => {
    setSearchQuery('');
    setSearchResults(null);
    setSearchParams({ doc: slug });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Go back to browse
  const goBack = () => {
    setSearchParams({});
    setSelectedDoc(null);
  };

  // Record feedback
  const handleFeedback = async (isHelpful) => {
    if (!selectedDoc) return;
    try {
      await apiHelpers.recordHelpFeedback(selectedDoc.slug, isHelpful);
      setFeedbackGiven(prev => ({ ...prev, [selectedDoc.slug]: true }));
    } catch (error) {
      console.error('Failed to record feedback:', error);
    }
  };

  // Get category meta or fallback
  const getCategoryMeta = (categoryKey) => {
    return CATEGORY_META[categoryKey] || {
      icon: BookOpenIcon,
      color: 'from-surface-400 to-surface-600',
      bgLight: 'bg-surface-50 dark:bg-surface-800',
      textColor: 'text-surface-600 dark:text-surface-400'
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-50 to-white dark:from-surface-950 dark:to-surface-900">
      {/* Header */}
      <div className="bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <BookOpenIcon className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-xl font-display font-bold text-surface-900 dark:text-white">
                  Neoshift BI Docs
                </h1>
                <p className="text-xs text-surface-500">Documentation</p>
              </div>
            </button>
            <div className="flex items-center gap-3">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="btn-ghost text-sm"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="btn-primary text-sm"
                  >
                    Get Started
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/dashboards')}
                  className="btn-primary text-sm"
                >
                  Go to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ===== Document Detail View ===== */}
        {activeSlug ? (
          <div>
            {/* Back button */}
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-surface-600 dark:text-surface-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-8 font-medium"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back to all topics
            </button>

            {docLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : selectedDoc ? (
              <div className="max-w-4xl mx-auto">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-surface-500 mb-4">
                  <button onClick={goBack} className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    Docs
                  </button>
                  <ChevronRightIcon className="w-3 h-3" />
                  <span className="capitalize">{selectedDoc.category?.replace('_', ' ')}</span>
                  <ChevronRightIcon className="w-3 h-3" />
                  <span className="text-surface-900 dark:text-white font-medium">{selectedDoc.title}</span>
                </div>

                {/* Title */}
                {(() => {
                  const meta = getCategoryMeta(selectedDoc.category);
                  const IconComponent = meta.icon;
                  return (
                    <div className="flex items-start gap-4 mb-8">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h1 className="text-3xl md:text-4xl font-display font-bold text-surface-900 dark:text-white mb-2">
                          {selectedDoc.title}
                        </h1>
                        {selectedDoc.excerpt && (
                          <p className="text-lg text-surface-600 dark:text-surface-400">
                            {selectedDoc.excerpt}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Content */}
                <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 p-8 md:p-10">
                  <div className="prose dark:prose-invert max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={markdownComponents}
                    >
                      {selectedDoc.content}
                    </ReactMarkdown>
                  </div>
                </div>

                {/* Feedback */}
                <div className="mt-8 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-surface-600 dark:text-surface-400 font-medium">
                      Was this article helpful?
                    </p>
                    {feedbackGiven[selectedDoc.slug] ? (
                      <p className="text-primary-600 dark:text-primary-400 font-medium">
                        Thanks for your feedback!
                      </p>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleFeedback(true)}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-green-400 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 text-surface-600 dark:text-surface-400 hover:text-green-600 dark:hover:text-green-400 transition-all text-sm font-medium"
                        >
                          <HandThumbUpIcon className="w-4 h-4" />
                          Yes
                        </button>
                        <button
                          onClick={() => handleFeedback(false)}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-red-400 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-surface-600 dark:text-surface-400 hover:text-red-600 dark:hover:text-red-400 transition-all text-sm font-medium"
                        >
                          <HandThumbDownIcon className="w-4 h-4" />
                          No
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Related docs in same category */}
                {(() => {
                  const sameCat = categories.find(c => c.category === selectedDoc.category);
                  const relatedDocs = sameCat?.documents?.filter(d => d.slug !== selectedDoc.slug) || [];
                  if (relatedDocs.length === 0) return null;

                  return (
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">
                        More in {sameCat.label}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {relatedDocs.map(doc => (
                          <button
                            key={doc.slug}
                            onClick={() => openDoc(doc.slug)}
                            className="text-left p-4 rounded-xl border border-surface-200 dark:border-surface-800 hover:border-primary-400 dark:hover:border-primary-500 bg-white dark:bg-surface-900 hover:shadow-md transition-all group"
                          >
                            <p className="font-medium text-surface-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                              {doc.title}
                            </p>
                            {doc.excerpt && (
                              <p className="text-xs text-surface-500 mt-1 line-clamp-2">
                                {doc.excerpt}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-20">
                <QuestionMarkCircleIcon className="w-16 h-16 text-surface-300 dark:text-surface-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-surface-900 dark:text-white mb-2">Document not found</h3>
                <p className="text-surface-500 mb-4">This article may have been removed or is not yet published.</p>
                <button onClick={goBack} className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
                  Browse all documentation
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ===== Browse / Search View ===== */
          <div>
            {/* Hero */}
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h1 className="text-5xl md:text-6xl font-display font-bold text-surface-900 dark:text-white mb-6">
                How can we help you?
              </h1>
              <p className="text-xl text-surface-600 dark:text-surface-400 mb-8">
                Search our documentation or browse categories below to learn how to get the most out of Neoshift BI.
              </p>

              {/* Search Bar */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
                <input
                  type="text"
                  placeholder="Search documentation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:outline-none focus:border-primary-500 dark:focus:border-primary-400 text-lg"
                />
                {searchLoading && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>

            {/* Search Results */}
            {searchResults !== null ? (
              <div className="max-w-4xl mx-auto mb-12">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-surface-900 dark:text-white">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
                  </h2>
                  <button
                    onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                    className="text-sm text-primary-600 dark:text-primary-400 font-medium hover:underline"
                  >
                    Clear search
                  </button>
                </div>

                {searchResults.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800">
                    <QuestionMarkCircleIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
                    <p className="text-surface-600 dark:text-surface-400">No articles found matching your search.</p>
                    <p className="text-sm text-surface-500 mt-1">Try different keywords or browse the categories below.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {searchResults.map(doc => {
                      const meta = getCategoryMeta(doc.category);
                      return (
                        <button
                          key={doc.slug}
                          onClick={() => openDoc(doc.slug)}
                          className="w-full text-left p-5 rounded-xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-lg transition-all group"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 px-2.5 py-1 rounded-md text-xs font-medium capitalize ${meta.bgLight} ${meta.textColor}`}>
                              {doc.category?.replace('_', ' ')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-surface-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                {doc.title}
                              </p>
                              {doc.excerpt && (
                                <p className="text-sm text-surface-500 mt-1 line-clamp-2">
                                  {doc.excerpt}
                                </p>
                              )}
                            </div>
                            <ChevronRightIcon className="w-5 h-5 text-surface-400 group-hover:text-primary-500 flex-shrink-0 mt-0.5" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : null}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : categories.length === 0 ? (
              /* Empty State */
              <div className="text-center py-20">
                <BookOpenIcon className="w-16 h-16 text-surface-300 dark:text-surface-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-surface-900 dark:text-white mb-2">
                  Documentation coming soon
                </h3>
                <p className="text-surface-500 max-w-md mx-auto">
                  We're working on building out our documentation. Check back soon for guides, tutorials, and more.
                </p>
              </div>
            ) : (
              /* Documentation Categories */
              <div>
                <h2 className="text-3xl font-display font-bold text-center text-surface-900 dark:text-white mb-8">
                  Browse Documentation
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {categories.map((cat) => {
                    const meta = getCategoryMeta(cat.category);
                    const IconComponent = meta.icon;

                    return (
                      <div
                        key={cat.category}
                        className="bg-white dark:bg-surface-900 rounded-2xl border-2 border-surface-200 dark:border-surface-800 hover:border-primary-400 dark:hover:border-primary-500 transition-all hover:shadow-xl p-6"
                      >
                        {/* Category Header */}
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center flex-shrink-0`}>
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-surface-900 dark:text-white mb-1">
                              {cat.label}
                            </h3>
                            <p className="text-sm text-surface-500">
                              {cat.documents.length} article{cat.documents.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>

                        {/* Document list */}
                        <div className="space-y-1">
                          {cat.documents.map((doc) => (
                            <button
                              key={doc.slug}
                              onClick={() => openDoc(doc.slug)}
                              className="w-full text-left p-3 rounded-lg hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors group"
                            >
                              <div className="flex items-center justify-between">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-surface-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
                                    {doc.title}
                                  </p>
                                  {doc.excerpt && (
                                    <p className="text-xs text-surface-500 mt-0.5 truncate">
                                      {doc.excerpt}
                                    </p>
                                  )}
                                </div>
                                <ChevronRightIcon className="w-4 h-4 text-surface-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 group-hover:translate-x-1 transition-all flex-shrink-0 ml-2" />
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Help Section */}
            <div className="mt-16 text-center bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-12">
              <QuestionMarkCircleIcon className="w-16 h-16 text-white mx-auto mb-4" />
              <h2 className="text-3xl font-display font-bold text-white mb-4">
                Still need help?
              </h2>
              <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
                Can't find what you're looking for? Our support team is here to help you succeed.
              </p>
              <div className="flex items-center justify-center gap-4">
                {isAuthenticated ? (
                  <button
                    onClick={() => navigate('/help')}
                    className="px-8 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors shadow-xl"
                  >
                    Contact Support
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => navigate('/register')}
                      className="px-8 py-3 bg-white text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors shadow-xl"
                    >
                      Get Started Free
                    </button>
                    <button
                      onClick={() => navigate('/pricing')}
                      className="px-8 py-3 bg-primary-700 text-white rounded-xl font-semibold hover:bg-primary-800 transition-colors"
                    >
                      View Pricing
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-surface-100 dark:bg-surface-900 border-t border-surface-200 dark:border-surface-800 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-surface-600 dark:text-surface-400">
              © 2026 Neoshift BI. All rights reserved.
            </div>
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
              >
                Home
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-sm text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
